import { Difference, Warning } from '@mui/icons-material';
import { NOT_APPLICABLE_LABEL } from '@/constants';
import { TemplateValidationError } from './types';
import ExcelJS from 'exceljs';

export const csvStringToJson = (
  csvString: string
): {
  header: string[];
  rows: Array<Record<string, unknown>>;
} => {
  // remove trailing newline at the end of the file
  if (csvString.endsWith('\n')) {
    csvString = csvString.slice(0, -1);
  }

  const lines = csvString.split('\n');
  const header = lines[0].trim().split(',');
  const rows = lines.slice(1).map((line) => {
    const row = line.split(',');
    return header.reduce(
      (accumulator, key, index) => {
        accumulator[key] = row[index];
        return accumulator;
      },
      {} as Record<string, unknown>
    );
  });
  return { header, rows };
};

export const getCleanCellValue = (value: number | string): string => {
  const stringifiedValue = String(value);
  if (!stringifiedValue || stringifiedValue.trim() === '') return NOT_APPLICABLE_LABEL;
  return stringifiedValue;
};

export const pickErrorIconForCell = ({
  rowError,
}: {
  rowError: TemplateValidationError | undefined;
}) => {
  if (rowError?.errorMap?.CUSTOM_DUP) {
    return <Difference color="warning" sx={{ fontSize: '1.6rem' }} />;
  }

  return <Warning color="error" sx={{ fontSize: '1.6rem' }} />;
};

export const xlsxBase64ToJson = async (base64Data: string): Promise<{ header: string[]; rows: string[][] }> => {
  try {
    const base64Content = base64Data.replace(/^data:.*;base64,/, '');
    
    const binaryString = atob(base64Content);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    
    const workbook = new ExcelJS.Workbook();
    
    await workbook.xlsx.load(bytes.buffer);
    
    const worksheet = workbook.worksheets[0];
    
    if (!worksheet) {
      throw new Error('No worksheet found in the Excel file');
    }
    
    const header: string[] = [];
    worksheet.getRow(1).eachCell((cell) => {
      header.push(cell.value?.toString() || '');
    });
    
    const rows: string[][] = [];
    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber === 1) return;
      
      const rowData: string[] = [];
      row.eachCell((cell) => {
        rowData.push(cell.value?.toString() || '');
      });
      
      rows.push(rowData);
    });
    
    return { header, rows };
  } catch (error) {
    return {
      header: [],
      rows: []
    };
  }
};
